import { COMPLIANCE_FIX_PRELUDE, type ComplianceFix } from '@bartizo/shared';
import { safeFixFor } from './safe-fixes.js';

/**
 * The fixes a compliance view lists, as a shell script to take to the server
 * (P11.17, made runnable in P11.18).
 *
 * Bartizo never runs a benchmark's remediation: it is prose, it differs per check,
 * and some of it (SSH, the firewall, mounts, the bootloader) can lock a server out.
 * So the script does two things. For the few fixes in `safe-fixes.ts` — small,
 * repeatable, reachable afterwards — it holds the real commands. For everything
 * else it holds the benchmark's words as comments, under a heading that says a
 * person has to do them.
 *
 * It changes nothing until it is told to: run as it is, it only prints what each
 * fix would do; `--apply` does them, one at a time, a failure does not stop the
 * rest, and every file it edits gets a `.bartizo.bak` copy first.
 */

const comment = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .map((line) => (line === '' ? '#' : `# ${line}`));

/** A shell word for the heading: single quotes, so nothing in a title is run. */
const quoted = (text: string): string => `'${text.replace(/'/g, `'\\''`)}'`;

function describe(fix: ComplianceFix, index: number): string[] {
  const where = fix.servers.map((server) => server.name);
  return [
    `# ${'-'.repeat(70)}`,
    `# ${String(index)}. ${fix.title}`,
    `# Check ${fix.policyId} / ${String(fix.checkId)}`,
    `# Controls: ${fix.controls.join(', ')}`,
    `# Failing on: ${where.join(', ')}${fix.serverCount > where.length ? ` and ${String(fix.serverCount - where.length)} more` : ''}`,
    ...(fix.rationale === null ? [] : ['#', '# Why it matters:', ...comment(fix.rationale)]),
  ];
}

export function fixRunbook(options: {
  standard: string;
  scope: string;
  fixes: readonly ComplianceFix[];
  now: Date;
}): string {
  const { standard, scope, fixes, now } = options;
  const automatic = fixes.filter((fix) => safeFixFor(fix.title) !== undefined);
  const manual = fixes.filter((fix) => safeFixFor(fix.title) === undefined);

  const lines = [
    '#!/usr/bin/env bash',
    `# Bartizo fixes: ${standard}, ${scope}`,
    `# Made ${now.toISOString()} from the failing checks of the configuration assessment.`,
    '#',
    '# Run it as it is and it only says what it would do:   sudo bash <this file>',
    '# Run it with --apply and it does it:                   sudo bash <this file> --apply',
    '#',
    `# ${String(automatic.length)} of ${String(fixes.length)} fixes are made here: small ones that are safe to repeat and`,
    '# leave the server reachable. A file they edit is first copied to <file>.bartizo.bak (once).',
    `# The ${manual.length === 1 ? 'other one is' : `other ${String(manual.length)} are`} only described below, as comments: the firewall, mounts, the bootloader`,
    '# and the like can lock a server out, so a person has to do those, one at a time.',
    '',
    'set -u',
    '',
    'APPLY=0',
    '[ "${1:-}" = "--apply" ] && APPLY=1',
    '[ "$(id -u)" -eq 0 ] || { echo "Run this as root: sudo bash $0 [--apply]"; exit 1; }',
    '',
    'FAILED=0',
    'fix() {',
    '  printf \'\\n== %s\\n   %s\\n\' "$1" "$2"',
    '  if [ "$APPLY" -eq 1 ]; then',
    '    if ( set -e; eval "$3" ); then echo "   done"; else echo "   FAILED, going on with the rest"; FAILED=$((FAILED + 1)); fi',
    '  else',
    "    printf '%s\\n' \"$3\" | sed 's/^/   would run: /'",
    '  fi',
    '}',
    '',
    '# Removes packages only when apt says nothing else would go with them: $1 is a regex of',
    '# the package names that may be removed, the rest are the packages (or patterns) to remove.',
    COMPLIANCE_FIX_PRELUDE,
    '',
    '[ "$APPLY" -eq 1 ] || echo "Dry run: nothing is changed. Run again with --apply to do these."',
    '',
  ];

  automatic.forEach((fix, index) => {
    const safe = safeFixFor(fix.title);
    if (safe === undefined) return;
    lines.push(...describe(fix, index + 1));
    lines.push(`fix ${quoted(fix.title)} ${quoted(safe.does)} ${quoted(safe.commands)}`, '');
  });

  lines.push(
    'if [ "$APPLY" -eq 1 ]; then printf \'\\nFinished, %s failed.\\n\' "$FAILED"; fi',
    'exit 0',
    '',
    `# ${'='.repeat(70)}`,
    `# NOT DONE BY THIS SCRIPT: ${String(manual.length)} fixes for a person to make, as the benchmark says them.`,
    '# Read each one, try it on one server first, and keep a second session open.',
    `# ${'='.repeat(70)}`,
    '',
  );
  manual.forEach((fix, index) => {
    lines.push(...describe(fix, automatic.length + index + 1));
    lines.push(
      '#',
      '# The fix, as the benchmark says it:',
      ...(fix.remediation === null
        ? ['# (the benchmark gives no remediation text for this check)']
        : comment(fix.remediation)),
      '',
    );
  });
  return `${lines.join('\n')}\n`;
}
